import * as Sentry from '@sentry/browser';

import {
  getReceiptTemplateIdByDocType,
  getUseReceiptTemplates,
} from 'reducers/configs/settings';
import { INTEGRATION_TYPES } from 'constants/CAFA';
import {
  getCafaEntry,
  getGeneralPrintingSettings,
  getPrintingMSSettings,
} from 'reducers/cafaConfigs';
import { getPluginLifecycleHook } from 'reducers/Plugins';
import { getSalesDocuments } from 'services/ErplyAPI/sales';
import { doClientRequest } from 'services/ErplyAPI/core/ErplyAPI';
import {
  getPrinterList,
  getSelectedPrinter,
} from 'services/InstallerApp/printingMS';
import { CancelActionOverrideError } from 'plugins/pluginUtils';
import { SaleDocumentResponse } from 'types/SalesDocument';
import { createLazyPromise } from 'utils';
import { STATISTIC_ENTRY_TYPES } from 'utils/statistics/Statistics';

import {
  AwaitedGenerateReceiptReturn,
  GenerateReceiptParams,
  GenerateReceiptReturn,
  SalesDocument,
} from './types';
import { renderARHtmlForPrintMS } from './actions';
import {
  getIntegrationType,
  getFinalPatchScript,
  getSaleDocID,
  withPrintingStatisticsAction,
} from './utils';

/**
 * Determine the paper width (in pixels) of the selected printer
 * Tries to determine the selected printer by comparing the full list of printers
 * to the provided CAFA configuration.
 * If an unambiguous match can't be found, rejects with an error
 */
async function getPixelsPerLineLegacy(
  settings: ReturnType<typeof getPrintingMSSettings>,
): Promise<number> {
  if (!settings) {
    throw new Error(
      'No CAFA settings found for printing-microservice, cannot determine selected printer',
    );
  }
  // From the settings, we know the name and vendor of the selected printer, and the paper size
  const {
    PrinterName: name,
    PrinterVendor: vendor,
    PrinterGetFullList: getFullList,
    PrinterPaperSize: paperSize,
  } = settings?.value ?? {};

  // So we fetch details of all the printers
  const printers = await getPrinterList(getFullList);
  // And find one with a matching name...
  let selectedPrinter = printers.find(p => p.Name === name);
  // ...Or the only printer with matching vendorID
  if (!selectedPrinter) {
    const candidates = printers.filter(p => p.VendorID === vendor);
    if (candidates.length === 1) {
      [selectedPrinter] = candidates;
    }
  }
  // ...Or the only printer in the whole list
  if (!selectedPrinter) {
    if (printers.length === 1) {
      [selectedPrinter] = printers;
    }
  }
  // And get the pixel width from that
  if (selectedPrinter) {
    return selectedPrinter?.[`PixelPerLine${paperSize}`];
  }

  // If we can't find the selected printer (f.ex. name doesn't match, and multiple printers have the same vendor)
  // then we don't know what width to use.
  // TODO: Handle cases where there are multiple matches, but they all have the same paper width?
  Sentry.setContext('goMSSettings', settings?.value);
  printers.forEach(printer => Sentry.setContext(printer.Name, printer));
  Sentry.captureException(
    new Error('Selected printer not present in getPrinterList response'),
  );
  throw new Error(
    `Configured printer is ${name}@${vendor}, but such a printer was not found in the list (${printers
      .map(p => `${p.Name}@${p.VendorID}`)
      .join(', ')})`,
  );
}

const getPatchScriptImageFromHtml = (html: string) => async (
  dispatch,
  getState,
) => {
  const {
    forcePatchscriptRender: { enabled, pixelWidth, scaleMultiplier },
  } = getGeneralPrintingSettings(getState());

  // Render using configured width and scale
  if (enabled) {
    const blob = await renderARHtmlForPrintMS(
      html,
      pixelWidth,
      scaleMultiplier,
    );
    return [{ type: 'image', blob }];
  }

  const printerWidth =
    // New endpoint gives selected printer data directly
    await getSelectedPrinter()
      .then(
        (printer): number =>
          printer[`PixelPerLine${printer.Options.papersize}`],
      )
      .catch(e => {
        // If new endpoint is not present, try the legacy method of determining paper width based on CAFA config + printer list
        const goMSSettings = getPrintingMSSettings(getState());
        return getPixelsPerLineLegacy(goMSSettings).catch(e2 => {
          // If both failed, join the errors together and report to console.
          // eslint-disable-next-line no-param-reassign
          (e2 as Error).cause = new Error('getSelectedPrinter request failed', {
            cause: e,
          });
          // eslint-disable-next-line no-console
          console.error(
            new Error('Legacy paper width calculation failed', { cause: e2 }),
          );
          return null;
        });
      });
  if (typeof printerWidth !== 'number') {
    // If we couldn't determine printerWidth, proceed anyway with null. This will make an arbitrarily sized receipt
    // but that's better than no receipt at all

    // eslint-disable-next-line no-console
    console.warn('Paper width unknown, rendering receipt at arbitrary width');
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const blob = await renderARHtmlForPrintMS(html, printerWidth!);
    return [{ type: 'image', blob }];
  }
  const blob = await renderARHtmlForPrintMS(html, printerWidth);
  return [{ type: 'image', blob }];
};

async function getFullSalesDoc(salesDoc) {
  function getParams() {
    // there will be no id available when sale was made in offline mode
    if (salesDoc.id) {
      return {
        id: salesDoc.id,
      };
    }
    if (salesDoc.invoiceID) {
      return {
        id: salesDoc.invoiceID,
      };
    }
    return {
      number: salesDoc.invoiceNo,
    };
  }

  const docs = await getSalesDocuments(getParams());
  return docs?.[0] ?? {};
}

/**
 * Sales document object passed to `printReceipt` can be of different types
 * and depending on the particular type we may or may not need to fetch
 * the sale from API to get missing props. This function fetches the sale
 * only when necessary and caches the response from the API.
 */
export function getCachingSalePropAccessFn(salesDoc: SalesDocument) {
  let cachedSalesDoc: SaleDocumentResponse | null = null;

  return async function getPropOrFetchIfMissing(
    propName: keyof SaleDocumentResponse,
  ): Promise<SaleDocumentResponse[typeof propName]> {
    if (salesDoc[propName]) {
      return salesDoc[propName];
    }
    if (cachedSalesDoc?.[propName]) {
      return cachedSalesDoc[propName];
    }
    const fullSalesDoc = await getFullSalesDoc(salesDoc);
    cachedSalesDoc = fullSalesDoc;
    return cachedSalesDoc[propName];
  };
}

/**
 * Given a sales document, gathers up the necessary data for it and outputs
 * an object of lazy promises that resolve with receipt data.
 * May make new network requests to fetch additional data about the document
 */
const generateReceiptBase = ({
  salesDocument,
  options = {},
}: GenerateReceiptParams) => async (
  dispatch,
  getState,
): Promise<GenerateReceiptReturn> => {
  const { before, on, after } = getPluginLifecycleHook('onGenerateReceipt')(
    getState(),
  );
  const integrationType = getIntegrationType(options);
  const useReceiptTemplates = getUseReceiptTemplates(getState());
  const type = INTEGRATION_TYPES.printer;

  try {
    await dispatch(before({ salesDocument, options }));

    const getSaleProp = getCachingSalePropAccessFn(salesDocument);

    const useMS =
      getCafaEntry<typeof type, 'goMS', { enabled: boolean }>(
        'goMS',
        type,
      )(getState())?.value.enabled ?? false;
    const useWrapper =
      getCafaEntry<typeof type, 'wrapper', { enabled: boolean }>(
        'wrapper',
        type,
      )(getState())?.value.enabled ?? false;

    const saleType = await getSaleProp('type');
    const receiptTemplateId = getReceiptTemplateIdByDocType(
      options.giftReceipt ? 'GIFTRECEIPT' : saleType,
    )(getState());

    const hasActualReportsTemplate =
      !!useReceiptTemplates && !!receiptTemplateId;

    const html = createLazyPromise(async () => {
      if (!hasActualReportsTemplate) {
        return undefined;
      }
      const saleId = await getSaleProp('id');
      return doClientRequest<{ html: string }>({
        request: 'getSalesDocumentActualReportsHTML',
        documentID: saleId,
        templateID: receiptTemplateId,
      }).then(records => records[0]?.html);
    });

    const onlyBrowserPrint =
      saleType === 'INVWAYBILL' ||
      integrationType === 'order' ||
      integrationType === 'quote';

    if (onlyBrowserPrint) {
      if (hasActualReportsTemplate) {
        const result = {
          html,
          onlyBrowserPrint: Promise.resolve(onlyBrowserPrint),
          hasActualReportsTemplate: Promise.resolve(hasActualReportsTemplate),
          patchScript: Promise.resolve(undefined),
          url: Promise.resolve(undefined),
          saleDocumentId: Promise.resolve(undefined),
        };
        return result;
      }
      const url = createLazyPromise(
        (): Promise<string> => getSaleProp('invoiceLink'),
      );
      const result = {
        url,
        onlyBrowserPrint: Promise.resolve(onlyBrowserPrint),
        hasActualReportsTemplate: Promise.resolve(hasActualReportsTemplate),
        patchScript: Promise.resolve(undefined),
        html: Promise.resolve(undefined),
        saleDocumentId: Promise.resolve(undefined),
      };
      return result;
    }

    const saleDocumentId = createLazyPromise(async () => {
      if (hasActualReportsTemplate) {
        return undefined;
      }
      const saleDocumentId =
        getSaleDocID(salesDocument) ?? (await getSaleProp('id'));
      return Number(saleDocumentId);
    });

    const url = createLazyPromise(
      (): Promise<string> => getSaleProp('receiptLink'),
    );

    const patchScript = createLazyPromise(async () => {
      const useHtmlToGeneratePatchScript =
        (useMS || useWrapper) && hasActualReportsTemplate;
      if (useHtmlToGeneratePatchScript) {
        return dispatch(getPatchScriptImageFromHtml((await html) ?? ''));
      }
      return dispatch(getFinalPatchScript(salesDocument, options));
    });

    const receipt = await dispatch(
      on(
        { salesDocument, options },
        {
          html,
          patchScript,
          url,
          hasActualReportsTemplate: Promise.resolve(hasActualReportsTemplate),
          onlyBrowserPrint: Promise.resolve(onlyBrowserPrint),
          saleDocumentId,
        },
      ),
    );
    await dispatch(after({ salesDocument, options }, null));

    return receipt;
  } catch (error) {
    const e = error as Error;
    if (e.cause instanceof CancelActionOverrideError) {
      const receipt = e.cause.data as AwaitedGenerateReceiptReturn; // Receipt generated by plugin hook
      const receiptAsPromises = Object.entries(receipt).map(([key, value]) => [
        key,
        Promise.resolve(value),
      ]);
      const keys = [
        'html',
        'url',
        'patchScript',
        'onlyBrowserPrint',
        'saleDocumentId',
        'hasActualReportsTemplate',
      ];
      const missingEntries = keys
        .filter(key => !(key in receipt))
        .map(key => [key, Promise.resolve(undefined)]);
      return Object.fromEntries([...receiptAsPromises, ...missingEntries]);
    }
    throw e;
  }
};

export const generateReceipt = withPrintingStatisticsAction({
  description: 'Generating receipt',
  itemType: STATISTIC_ENTRY_TYPES.TypeNameDataProcessing,
})(generateReceiptBase);
